import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart3, Users, TrendingUp, TrendingDown, Target, Award, AlertTriangle,
  CheckCircle2, FileDown, FileSpreadsheet, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, CartesianGrid,
} from "recharts";
import { exportAnalysisExcel } from "@/lib/excel";
import { downloadCertificatePdf, downloadBulkCertificatesPdf } from "@/lib/pdf";

const CERT_TYPE_LABELS = { semester1: "الفصل الدراسي الأول", semester2: "الفصل الدراسي الثاني", end_year: "نهاية العام" };

/**
 * Analysis — لوحة التحليل الإحصائي والتربوي المتكاملة.
 * تُبنى بالكامل فوق GET /analysis (المصدر الوحيد لأي رقم هنا — لا حساب
 * درجات محليًا في الواجهة إطلاقًا). التحليل الكيفي والتوصيات والاستنتاج
 * العام نصوص تُبنى ديناميكيًا من نفس أرقام الـ API، وليست نصوصًا ثابتة.
 */
export default function Analysis() {
  const { ctx, meta } = useApp();
  const grades = meta?.grades_by_stage?.[ctx.stage] || [];

  const [grade, setGrade] = useState("");
  const [classFilter, setClassFilter] = useState("__all__");
  const [subjectFilter, setSubjectFilter] = useState("__all__");
  const [certType, setCertType] = useState("semester1");
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [settings, setSettings] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!grade) { setClasses([]); return; }
    api.get("/students/classes", { params: { stage: ctx.stage, year: ctx.year, grade } })
      .then(({ data }) => setClasses(data)).catch(() => {});
  }, [ctx.stage, ctx.year, grade]);

  // قائمة المواد لكلا الفصلين مجمَّعة (بلا تكرار بالاسم) — لأن "نهاية
  // العام" قد تشمل مواد أُضيفت في فصل دون الآخر.
  useEffect(() => {
    if (!grade) { setSubjects([]); return; }
    Promise.all([
      api.get("/subjects", { params: { stage: ctx.stage, year: ctx.year, grade, semester: 1 } }),
      api.get("/subjects", { params: { stage: ctx.stage, year: ctx.year, grade, semester: 2 } }),
    ]).then(([r1, r2]) => {
      const names = new Set();
      const merged = [];
      [...r1.data, ...r2.data].forEach((s) => {
        if (!names.has(s.subject_name)) { names.add(s.subject_name); merged.push(s); }
      });
      setSubjects(merged);
    }).catch(() => {});
  }, [ctx.stage, ctx.year, grade]);

  const load = async () => {
    if (!grade) return toast.error("اختر الصف أولاً");
    setLoading(true);
    try {
      const { data } = await api.get("/analysis", {
        params: {
          stage: ctx.stage, year: ctx.year, grade, cert_type: certType,
          ...(classFilter !== "__all__" ? { class_name: classFilter } : {}),
          ...(subjectFilter !== "__all__" ? { subject_name: subjectFilter } : {}),
        },
      });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const qualitativeText = useMemo(() => buildQualitativeText(data), [data]);
  const conclusion = useMemo(() => buildConclusion(data), [data]);
  const recommendation = useMemo(() => buildRecommendation(data), [data]);

  const bandChartData = data ? data.band_order.map((lbl) => ({
    label: lbl,
    count: data.band_distribution_detailed[lbl].count,
    percentage: data.band_distribution_detailed[lbl].percentage,
    color: data.band_distribution_detailed[lbl].color,
  })) : [];

  const meta_ = {
    schoolName: settings?.school_name || "المدرسة",
    year: ctx.year, stage: ctx.stage, grade,
    className: classFilter !== "__all__" ? classFilter : "",
    subjectName: subjectFilter !== "__all__" ? subjectFilter : "",
    certTypeLabel: CERT_TYPE_LABELS[certType],
  };

  const exportExcel = () => {
    if (!data) return toast.error("نفّذ التحليل أولًا");
    exportAnalysisExcel(data, meta_, qualitativeText, recommendation.text,
      `التحليل-الإحصائي-${grade}.xlsx`);
  };

  const exportPdf = async () => {
    if (!data) return toast.error("نفّذ التحليل أولًا");
    setExporting(true);
    const container = document.createElement("div");
    container.setAttribute("dir", "rtl");
    container.style.position = "fixed";
    container.style.left = "-99999px";
    container.style.top = "0";
    container.style.background = "#ffffff";
    document.body.appendChild(container);
    try {
      const { createRoot } = await import("react-dom/client");
      const pages = [AnalysisPdfPage1, AnalysisPdfPage2];
      const nodes = [];
      for (const Comp of pages) {
        const wrap = document.createElement("div");
        wrap.style.width = "794px";
        wrap.style.height = "1123px";
        wrap.style.overflow = "hidden";
        wrap.style.background = "#ffffff";
        container.appendChild(wrap);
        const root = createRoot(wrap);
        await new Promise((resolve) => {
          root.render(
            <Comp school={settings} meta={meta_} data={data} bandChartData={bandChartData}
                  qualitativeText={qualitativeText} conclusion={conclusion} recommendation={recommendation} />
          );
          document.fonts.ready.then(() => setTimeout(resolve, 200));
        });
        nodes.push(wrap);
      }
      if (nodes.length === 1) await downloadCertificatePdf(nodes[0], `التحليل-الإحصائي-${grade}.pdf`);
      else await downloadBulkCertificatesPdf(nodes, `التحليل-الإحصائي-${grade}.pdf`);
      toast.success("تم توليد تقرير PDF بنجاح");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { container.remove(); setExporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="analysis-page">
      <PageHeader
        icon={BarChart3}
        overline="التقارير والشهادات"
        title="التحليل الإحصائي والتربوي"
        subtitle="لوحة تحليل متكاملة: مؤشرات كمية، تصنيف أداء، تفسير كيفي تلقائي، واستنتاجات وتوصيات مبنية من بيانات الطلاب الفعلية."
        actions={data && (
          <>
            <Button variant="outline" onClick={exportPdf} disabled={exporting}
                     className="h-10 bg-white/10 border-white/20 text-white hover:bg-white/15" data-testid="analysis-export-pdf">
              <FileDown className="h-4 w-4 me-1.5" /> {exporting ? "جارٍ التوليد…" : "تصدير PDF"}
            </Button>
            <Button variant="outline" onClick={exportExcel}
                     className="h-10 bg-white/10 border-white/20 text-white hover:bg-white/15" data-testid="analysis-export-excel">
              <FileSpreadsheet className="h-4 w-4 me-1.5" /> تصدير Excel
            </Button>
          </>
        )}
      />

      <div className="rounded-xl border border-border bg-white p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
        <SF label="الصف" value={grade} onChange={setGrade} options={grades} />
        <SF label="الفصل" value={classFilter} onChange={setClassFilter}
             options={["__all__", ...classes]} labels={{ __all__: "كل الفصول" }} />
        <SF label="المادة" value={subjectFilter} onChange={setSubjectFilter}
             options={["__all__", ...subjects.map((s) => s.subject_name)]} labels={{ __all__: "كل المواد" }} />
        <SF label="الفترة" value={certType} onChange={setCertType}
             options={["semester1", "semester2", "end_year"]} labels={CERT_TYPE_LABELS} />
        <Button onClick={load} disabled={!grade || loading} className="h-10" data-testid="analysis-load-btn">
          {loading ? "جارٍ التحليل…" : "تحديث التحليل"}
        </Button>
      </div>

      {!data && !loading && (
        <div className="rounded-xl border border-dashed border-border bg-white p-16 text-center text-muted-foreground">
          اختر الصف والفلاتر المطلوبة ثم اضغط "تحديث التحليل"
        </div>
      )}

      {data && (
        <>
          {data.excluded_count > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              تم تحليل نتائج {data.evaluated_count} طالبًا من أصل {data.count} طالبًا، بينما توجد {data.excluded_count} حالة لم تستكمل بياناتها أو لا تخص هذا التحديد.
            </div>
          )}

          {/* بطاقات المؤشرات الرئيسية */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <StatCard icon={Users} label="إجمالي الطلاب" value={data.count} tone="slate" />
            <StatCard icon={CheckCircle2} label="تم تقييمهم" value={data.evaluated_count} tone="slate" />
            <StatCard icon={TrendingUp} label="أعلى درجة" value={`${data.highest_percentage}%`} tone="emerald" />
            <StatCard icon={TrendingDown} label="أقل درجة" value={`${data.lowest_percentage}%`} tone="rose" />
            <StatCard icon={Target} label="المتوسط" value={`${data.average_percentage}%`} tone="primary" />
            <StatCard icon={BarChart3} label="الوسيط" value={`${data.median_percentage}%`} tone="slate" />
            <StatCard icon={Award} label="نسبة النجاح" value={`${data.success_rate}%`} tone="emerald" />
            <StatCard icon={Sparkles} label="نسبة الإتقان" value={`${data.mastery_rate}%`} tone="primary" />
          </div>

          {/* توزيع مستويات الأداء: جدول + رسمان بيانيان */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="rounded-xl border border-border bg-white p-5 lg:col-span-1">
              <h3 className="font-display text-base font-semibold mb-3">توزيع مستويات الأداء</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[12px] text-muted-foreground border-b border-border">
                    <th className="text-start py-2 font-medium">المستوى</th>
                    <th className="text-center py-2 font-medium">النطاق</th>
                    <th className="text-center py-2 font-medium">العدد</th>
                    <th className="text-center py-2 font-medium">النسبة</th>
                  </tr>
                </thead>
                <tbody>
                  {data.band_order.map((lbl) => {
                    const d = data.band_distribution_detailed[lbl];
                    return (
                      <tr key={lbl} className="border-b border-slate-100 last:border-0">
                        <td className="py-2">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: d.color }} />
                            {lbl}
                          </span>
                        </td>
                        <td className="py-2 text-center tabular-nums text-muted-foreground">{d.range}</td>
                        <td className="py-2 text-center tabular-nums font-semibold">{d.count}</td>
                        <td className="py-2 text-center tabular-nums">{d.percentage}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="rounded-xl border border-border bg-white p-5">
              <h3 className="font-display text-base font-semibold mb-3">توزيع النسب (Doughnut)</h3>
              <div className="h-64">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={bandChartData} dataKey="count" nameKey="label"
                          cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2}>
                      {bandChartData.map((d) => <Cell key={d.label} fill={d.color} />)}
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-white p-5">
              <h3 className="font-display text-base font-semibold mb-3">مقارنة الأعداد (Bar Chart)</h3>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={bandChartData} margin={{ top: 10, right: 12, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                    <XAxis dataKey="label" fontSize={10} interval={0} angle={-20} textAnchor="end" tickLine={false} axisLine={false} />
                    <YAxis fontSize={11} tickLine={false} axisLine={false} width={28} allowDecimals={false} />
                    <Tooltip cursor={{ fill: "#0F766E10" }} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {bandChartData.map((d) => <Cell key={d.label} fill={d.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* التحليل الكيفي */}
          <div className="rounded-xl border border-border bg-white p-6">
            <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" /> التحليل الكيفي
            </h3>
            <p className="text-[14px] leading-relaxed text-slate-700">{qualitativeText}</p>
          </div>

          {/* الاستنتاج العام */}
          <div className={`rounded-xl border p-6 ${conclusionTone(conclusion.tone)}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="font-display text-lg font-bold">الاستنتاج العام</div>
              <span className="text-[12px] font-semibold px-2.5 py-1 rounded-full bg-white/60">
                المستوى العام للفصل: {conclusion.level}
              </span>
            </div>
            <p className="text-[14px] leading-relaxed">{conclusion.text}</p>
          </div>

          {/* التوصيات */}
          <div className="rounded-xl border border-border bg-white p-6">
            <h3 className="font-display text-lg font-semibold mb-3">التوصيات التربوية</h3>
            <p className="text-[14px] leading-relaxed text-slate-700">{recommendation.text}</p>
          </div>

          {/* تحليل أعلى/أدنى 10 طلاب */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RankTable title="أعلى 10 طلاب" rows={data.top_10} tone="emerald" />
            <RankTable title="الطلاب الأكثر احتياجًا للدعم" rows={data.bottom_10} tone="rose" />
          </div>
        </>
      )}
    </div>
  );
}

// ══════════════════════ التحليل الكيفي / الاستنتاج / التوصيات — نصوص Dynamic ══════════════════════

function _pluralStudents(n) {
  if (n === 1) return "طالب واحد";
  if (n === 2) return "طالبان";
  return `${n} ${n <= 10 ? "طلاب" : "طالبًا"}`;
}

function buildQualitativeText(data) {
  if (!data || !data.evaluated_count) {
    return "لا توجد بيانات كافية لعرض تحليل كيفي — لم يتم تقييم أي طالب في هذا التحديد.";
  }
  const parts = data.band_order
    .map((lbl) => ({ lbl, ...data.band_distribution_detailed[lbl] }))
    .filter((p) => p.count > 0);
  if (!parts.length) return "لا توجد نتائج كافية لتحليلها كيفيًا.";

  const sorted = [...parts].sort((a, b) => b.count - a.count);
  const dominant = sorted[0];
  let text = `أظهرت نتائج التحليل أن غالبية الطلاب يحققون مستوى ${dominant.lbl}، حيث بلغ عدد الطلاب في هذا المستوى ${_pluralStudents(dominant.count)} بنسبة ${dominant.percentage}% من إجمالي الطلاب المُقيَّمين (${data.evaluated_count}).`;

  const others = parts.filter((p) => p.lbl !== dominant.lbl);
  others.forEach((p, i) => {
    const conj = i === 0 ? " بينما بلغ" : (i === others.length - 1 ? "، في حين بلغ" : "، وبلغ");
    text += `${conj} عدد الطلاب الذين حققوا مستوى ${p.lbl} ${_pluralStudents(p.count)} بنسبة ${p.percentage}%.`;
  });

  const lowestLbl = data.band_order[data.band_order.length - 1];
  const lowest = data.band_distribution_detailed[lowestLbl];
  if (lowest && lowest.count > 0 && lowestLbl !== dominant.lbl) {
    text += ` ويحتاج ${_pluralStudents(lowest.count)} إلى دعم إضافي في مستوى "${lowestLbl}"، مما يشير إلى ضرورة توجيه خطط علاجية مناسبة لهذه الفئة.`;
  }
  return text;
}

function buildConclusion(data) {
  if (!data || !data.evaluated_count) {
    return { level: "غير محدد", tone: "slate", text: "لا توجد بيانات كافية لاستخلاص استنتاج عام حول أداء الفصل." };
  }
  const order = data.band_order;
  const dist = data.band_distribution_detailed;
  const topCount = Math.min(2, order.length);
  const topPct = order.slice(0, topCount).reduce((s, l) => s + (dist[l]?.percentage || 0), 0);
  const lowPct = order.slice(-topCount).reduce((s, l) => s + (dist[l]?.percentage || 0), 0);

  let level, tone;
  if (topPct >= 60) { level = "أداء مرتفع"; tone = "emerald"; }
  else if (lowPct >= 40) { level = "يحتاج إلى تدخل"; tone = "rose"; }
  else { level = "أداء متوسط"; tone = "amber"; }

  const dominant = [...order].sort((a, b) => (dist[b]?.count || 0) - (dist[a]?.count || 0))[0];
  const text = `تشير المؤشرات الإحصائية إلى أن المستوى العام لأداء الطلاب هو "${level}"، حيث بلغ متوسط الدرجات ${data.average_percentage}% ونسبة النجاح ${data.success_rate}%. المستوى الأكثر انتشارًا بين الطلاب هو "${dominant}"، مع ${lowPct >= 40 ? "ارتفاع ملحوظ" : "وجود"} في نسبة الطلاب بالمستويات الدنيا (${lowPct.toFixed(1)}%)${topPct >= 40 ? `، مقابل ${topPct.toFixed(1)}% في المستويات العليا` : ""}.`;
  return { level, tone, text };
}

function buildRecommendation(data) {
  if (!data || !data.evaluated_count) {
    return { text: "لا توجد بيانات كافية لصياغة توصيات تربوية في الوقت الحالي." };
  }
  const order = data.band_order;
  const dist = data.band_distribution_detailed;
  const lowPct = order.slice(-2).reduce((s, l) => s + (dist[l]?.percentage || 0), 0);
  const topPct = order.slice(0, 2).reduce((s, l) => s + (dist[l]?.percentage || 0), 0);

  let text;
  if (lowPct >= 40) {
    text = `يوصى بتقديم برامج علاجية موجهة للطلاب منخفضي التحصيل (${lowPct.toFixed(1)}% من إجمالي الطلاب)، مع متابعة تقدمهم بصورة دورية، وتحديد الأسباب الجذرية لضعف الأداء عبر التواصل مع أولياء الأمور والمعلمين المختصين.`;
  } else if (topPct >= 60) {
    text = `تشير النتائج إلى مستوى أداء مرتفع (${topPct.toFixed(1)}% من الطلاب في المستويات العليا)، ويوصى بالمحافظة على مستوى التحصيل الحالي، وتقديم أنشطة إثرائية وتحديات إضافية للطلاب المتفوقين للحفاظ على تحفيزهم.`;
  } else {
    text = `يوصى بتحديد جوانب الضعف الأكاديمية لدى الطلاب في المستويات المتوسطة والدنيا، والعمل على معالجتها من خلال خطط دعم وإثراء مناسبة، مع الاستمرار في متابعة نسبة النجاح البالغة ${data.success_rate}%.`;
  }
  if (data.excluded_count > 0) {
    text += ` كما يُنصح بمتابعة حالات الطلاب غير المكتملة البيانات (${data.excluded_count} حالة) لضمان شمول جميع الطلاب في التقييم مستقبلًا.`;
  }
  return { text };
}

function conclusionTone(tone) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    rose: "border-rose-200 bg-rose-50 text-rose-900",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return tones[tone] || tones.slate;
}

// ══════════════════════ مكوّنات UI مساعدة ══════════════════════

function StatCard({ icon: Icon, label, value, tone }) {
  const tones = {
    primary: "bg-primary/8 text-primary",
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className="rounded-xl border border-border bg-white p-4">
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center mb-2 ${tones[tone]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function RankTable({ title, rows, tone }) {
  const toneColor = tone === "emerald" ? "text-emerald-700" : "text-rose-700";
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <h3 className="font-display text-base font-semibold mb-3">{title}</h3>
      {!rows || rows.length === 0 ? (
        <div className="text-center text-muted-foreground text-sm py-6">لا توجد بيانات</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[12px] text-muted-foreground border-b border-border">
              <th className="text-center py-2 font-medium w-10">#</th>
              <th className="text-start py-2 font-medium">الاسم</th>
              <th className="text-center py-2 font-medium">النسبة</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.student_id || i} className="border-b border-slate-100 last:border-0">
                <td className="py-2 text-center text-muted-foreground">{i + 1}</td>
                <td className="py-2">{r.name}</td>
                <td className={`py-2 text-center tabular-nums font-semibold ${toneColor}`}>{r.percentage}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SF({ label, value, onChange, options, labels }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[12px]">{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="h-10 bg-white"><SelectValue placeholder="اختر" /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{labels?.[o] ?? o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// ══════════════════════ صفحتا تقرير الـ PDF (A4 ثابت، بنفس أسلوب الشهادات) ══════════════════════

function PdfHeader({ school, meta }) {
  return (
    <div className="border-b-2 border-primary/50 pb-4 text-center space-y-1 relative">
      {school?.ministry_logo && (
        <img src={school.ministry_logo} alt="شعار الوزارة"
             className="absolute top-0 start-0 h-16 w-16 object-contain" crossOrigin="anonymous" />
      )}
      {school?.school_logo && (
        <img src={school.school_logo} alt="شعار المدرسة"
             className="absolute top-0 end-0 h-16 w-16 object-contain" crossOrigin="anonymous" />
      )}
      <div className="text-primary font-display text-xl font-bold">{school?.ministry_name || "وزارة التعليم"}</div>
      {school?.education_dept && <div className="text-primary font-medium">{school.education_dept}</div>}
      <div className="text-primary font-display text-lg font-bold">{meta.schoolName}</div>
      <h2 className="font-display text-lg font-bold text-primary mt-2">تحليل نتائج {meta.subjectName ? `مادة (${meta.subjectName})` : "الفصل"}</h2>
      <div className="text-sm mt-1">
        {meta.stage} — الصف: {meta.grade} — الفصل: {meta.className || "كل الفصول"} — {meta.certTypeLabel}
      </div>
      <div className="text-xs text-muted-foreground">العام الدراسي: {meta.year}</div>
    </div>
  );
}

function AnalysisPdfPage1({ school, meta, data, qualitativeText }) {
  return (
    <div className="mx-auto max-w-4xl h-full flex flex-col bg-white border-4 border-primary/70 rounded-xl p-8 shadow-sm" dir="rtl">
      <PdfHeader school={school} meta={meta} />
      <div className="mt-6">
        <h3 className="font-display text-base font-bold text-primary mb-2">الملخص الإحصائي</h3>
        <div className="grid grid-cols-4 gap-2">
          {[
            ["إجمالي الطلاب", data.count], ["تم تقييمهم", data.evaluated_count],
            ["أعلى درجة", `${data.highest_percentage}%`], ["أقل درجة", `${data.lowest_percentage}%`],
            ["المتوسط", `${data.average_percentage}%`], ["الوسيط", `${data.median_percentage}%`],
            ["نسبة النجاح", `${data.success_rate}%`], ["نسبة الإتقان", `${data.mastery_rate}%`],
          ].map(([label, value]) => (
            <div key={label} className="rounded-lg border border-slate-200 p-2 text-center">
              <div className="text-[10px] text-muted-foreground">{label}</div>
              <div className="font-display text-base font-bold">{value}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-6">
        <h3 className="font-display text-base font-bold text-primary mb-2">توزيع مستويات الأداء</h3>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-primary text-white">
              <th className="px-2 py-1.5 border border-primary text-start">المستوى</th>
              <th className="px-2 py-1.5 border border-primary">النطاق</th>
              <th className="px-2 py-1.5 border border-primary">عدد الطلاب</th>
              <th className="px-2 py-1.5 border border-primary">النسبة</th>
            </tr>
          </thead>
          <tbody>
            {data.band_order.map((lbl) => {
              const d = data.band_distribution_detailed[lbl];
              return (
                <tr key={lbl}>
                  <td className="px-2 py-1.5 border border-slate-200">{lbl}</td>
                  <td className="px-2 py-1.5 border border-slate-200 text-center">{d.range}</td>
                  <td className="px-2 py-1.5 border border-slate-200 text-center font-bold">{d.count}</td>
                  <td className="px-2 py-1.5 border border-slate-200 text-center">{d.percentage}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-6">
        <h3 className="font-display text-base font-bold text-primary mb-2">التحليل الكيفي</h3>
        <p className="text-[13px] leading-relaxed">{qualitativeText}</p>
      </div>
      <div className="mt-auto pt-6 text-center text-[11px] text-muted-foreground">صفحة 1 من 2</div>
    </div>
  );
}

function AnalysisPdfPage2({ school, meta, data, bandChartData, conclusion, recommendation }) {
  return (
    <div className="mx-auto max-w-4xl h-full flex flex-col bg-white border-4 border-primary/70 rounded-xl p-8 shadow-sm" dir="rtl">
      <PdfHeader school={school} meta={meta} />
      <div className="mt-6 grid grid-cols-2 gap-6">
        <div>
          <h3 className="font-display text-base font-bold text-primary mb-2 text-center">توزيع النسب</h3>
          <div style={{ height: 220 }}>
            {/* isAnimationActive={false}: بدون هذا، الرسم يبدأ برسم القطاع
                تدريجيًا (من صفر حتى الاكتمال خلال ~800ms افتراضيًا)، ولقطة
                html2canvas كانت تُلتقَط أثناء هذه الحركة فتظهر شريحة واحدة
                مظلَّلة فقط بدل الدائرة كاملة — هذا هو السبب الحقيقي لعدم
                اكتمال الرسم في الـ PDF رغم اكتماله على الشاشة (حيث الحركة
                تنتهي طبيعيًا قبل أن ينظر أحد للشاشة). */}
            <PieChart width={330} height={220}>
              <Pie data={bandChartData} dataKey="count" nameKey="label" cx="50%" cy="50%" innerRadius={45} outerRadius={80}
                   paddingAngle={2} isAnimationActive={false}>
                {bandChartData.map((d) => <Cell key={d.label} fill={d.color} />)}
              </Pie>
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </div>
        </div>
        <div>
          <h3 className="font-display text-base font-bold text-primary mb-2 text-center">مقارنة الأعداد</h3>
          <div style={{ height: 220 }}>
            <BarChart width={330} height={220} data={bandChartData} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
              <XAxis dataKey="label" fontSize={9} interval={0} angle={-20} textAnchor="end" tickLine={false} axisLine={false} />
              <YAxis fontSize={10} tickLine={false} axisLine={false} width={22} allowDecimals={false} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                {bandChartData.map((d) => <Cell key={d.label} fill={d.color} />)}
              </Bar>
            </BarChart>
          </div>
        </div>
      </div>
      <div className="mt-6">
        <h3 className="font-display text-base font-bold text-primary mb-2">الاستنتاج العام: {conclusion.level}</h3>
        <p className="text-[13px] leading-relaxed">{conclusion.text}</p>
      </div>
      <div className="mt-4">
        <h3 className="font-display text-base font-bold text-primary mb-2">التوصيات التربوية</h3>
        <p className="text-[13px] leading-relaxed">{recommendation.text}</p>
      </div>
      <div className="mt-auto pt-6 grid grid-cols-2 gap-4 text-sm">
        <div className="flex items-baseline gap-1.5 whitespace-nowrap">
          <span className="font-semibold text-primary">إعداد:</span>
          <span className="flex-1 border-b border-dotted border-slate-400">&nbsp;</span>
        </div>
        <div className="flex items-baseline gap-1.5 whitespace-nowrap">
          <span className="font-semibold text-primary">اعتماد مدير المدرسة:</span>
          <span className="flex-1 border-b border-dotted border-slate-400">&nbsp;</span>
        </div>
      </div>
    </div>
  );
}
